import { type FormEvent, useCallback, useEffect, useState } from 'react'
import { documentsApi, type DocumentSummary } from '../api/documents'
import { signaturesApi, type SignatureEvidenceView, type SignatureOverview, type SignatureStatus } from '../api/signatures'
import { dateIt, errMsg } from '../lib/financeFormat'
import { useFeatureFlag } from '../lib/featureFlags'
import {
  Button, Callout, Card, CardContent, CardHeader, DataTable, DataTableEmptyRow, DateField, IconButtonLink, Select, StatusPill, TextField,
  type PillTone,
} from './ui'

const STATUS_TONE: Record<SignatureStatus, PillTone> = {
  InAttesa: 'info', Firmata: 'success', Rifiutata: 'error', Revocata: 'neutral', Scaduta: 'neutral', Bloccata: 'error',
}
const STATUS_LABEL: Record<SignatureStatus, string> = {
  InAttesa: 'In attesa', Firmata: 'Firmata', Rifiutata: 'Non firmata', Revocata: 'Revocata', Scaduta: 'Scaduta', Bloccata: 'Bloccata',
}

function todayIso(): string {
  const d = new Date()
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`
}

/**
 * R5-02 (Z-12) — pagina pratica: firma del contratto. Il settore decide il sistema: firma Equal (link sicuro + codice
 * via email + PDF firmato con impronta) oppure sistema dell'operatore (si registra solo l'esito). Visibile col flag
 * `firma.equal`; un 403/404 la nasconde. Il link si vede una volta sola, appena creato.
 */
export function ContractSignaturePanel({ contractId }: { contractId: number }) {
  const enabled = useFeatureFlag('firma.equal')
  const [data, setData] = useState<SignatureOverview | null>(null)
  const [hidden, setHidden] = useState(false)
  const [error, setError] = useState('')
  const [busy, setBusy] = useState(false)
  const [docs, setDocs] = useState<DocumentSummary[]>([])
  const [docId, setDocId] = useState('')
  const [created, setCreated] = useState<{ url: string; inviteQueued: boolean } | null>(null)
  const [copied, setCopied] = useState(false)
  const [evidence, setEvidence] = useState<SignatureEvidenceView | null>(null)
  const [outSigned, setOutSigned] = useState('true')
  const [outDate, setOutDate] = useState(todayIso)
  const [outNote, setOutNote] = useState('')

  const load = useCallback(() => {
    signaturesApi.overview(contractId)
      .then(setData)
      .catch(e => { if (/403|404|forbidden|not found/i.test(errMsg(e, ''))) setHidden(true); else setError(errMsg(e, 'Firma non disponibile.')) })
  }, [contractId])

  useEffect(() => { if (enabled) load() }, [enabled, load])
  useEffect(() => {
    if (!enabled || data?.providerKind !== 'Online') return
    documentsApi.list('Contract', contractId)
      .then(list => { const pdf = list.filter(d => d.mimeType === 'application/pdf'); setDocs(pdf); if (pdf[0]) setDocId(String(pdf[0].id)) })
      .catch(() => setDocs([]))
  }, [enabled, contractId, data?.providerKind])

  if (!enabled || hidden || !data) return null
  const online = data.providerKind === 'Online'
  const pending = data.signatures.some(s => s.status === 'InAttesa')

  async function send() {
    if (!docId) return
    setBusy(true); setError(''); setCopied(false)
    try {
      const r = await signaturesApi.send(contractId, Number(docId))
      setCreated({ url: r.url, inviteQueued: r.inviteQueued })
      load()
    } catch (e) { setError(errMsg(e, 'Invio non riuscito.')) } finally { setBusy(false) }
  }

  async function revoke(id: number) {
    setBusy(true); setError('')
    try { await signaturesApi.revoke(contractId, id); load() } catch (e) { setError(errMsg(e, 'Revoca non riuscita.')) } finally { setBusy(false) }
  }

  async function showEvidence(id: number) {
    setError('')
    try { setEvidence(await signaturesApi.evidence(contractId, id)) } catch (e) { setError(errMsg(e, 'Registro non disponibile.')) }
  }

  async function saveOutcome(ev: FormEvent) {
    ev.preventDefault()
    setBusy(true); setError('')
    try {
      await signaturesApi.outcome(contractId, outSigned === 'true', outDate, outNote.trim() || null)
      setOutNote(''); load()
    } catch (e) { setError(errMsg(e, 'Esito non salvato.')) } finally { setBusy(false) }
  }

  async function copy() {
    if (!created) return
    try { await navigator.clipboard.writeText(created.url); setCopied(true) } catch { setCopied(false) }
  }

  const source = data.routeSource === 'regola' ? 'regola del settore' : 'impostazione predefinita'

  return (
    <Card variant="outlined" padding="none">
      <CardHeader
        icon="signature"
        title="Firma del contratto"
        subtitle={`${data.providerLabel} — ${data.departmentName ?? 'settore non indicato'} (${source}).${data.contractSignedAt ? ` Firmato il ${dateIt(data.contractSignedAt)}.` : ''}`}
      />
      <CardContent>
        {error && <Callout tone="error" className="mb-3">{error}</Callout>}
        {created && (
          <Callout tone="success" title="Contratto inviato: il link si vede solo ora" className="mb-3">
            <TextField label="Link di firma" value={created.url} readOnly onFocus={e => e.currentTarget.select()} />
            <div className="d-flex gap-2 align-items-center mt-2">
              <Button variant="outlined" icon="content_copy" onClick={() => void copy()}>{copied ? 'Copiato' : 'Copia'}</Button>
              <span className="text-muted md-typescale-body-small">
                {created.inviteQueued ? `Email di invito in uscita${data.recipientHint ? ` a ${data.recipientHint}` : ''}.` : 'Email di invito non accodata: invia il link a mano.'}
              </span>
            </div>
          </Callout>
        )}
        {online ? (
          <div className="d-flex gap-2 align-items-end flex-wrap">
            <div style={{ minWidth: 260, flex: 1 }}>
              <Select label="Documento da firmare (PDF)" value={docId} onChange={e => setDocId(e.currentTarget.value)} disabled={docs.length === 0}>
                {docs.length === 0 && <option value="">Nessun PDF tra i documenti della pratica</option>}
                {docs.map(d => <option key={d.id} value={d.id}>{d.name}</option>)}
              </Select>
            </div>
            <Button variant="tonal" icon="send" loading={busy} disabled={!docId || !!data.contractSignedAt} onClick={() => void send()}>
              {pending ? 'Invia di nuovo' : 'Invia per la firma'}
            </Button>
          </div>
        ) : (
          <form onSubmit={e => void saveOutcome(e)} className="d-flex gap-2 align-items-end flex-wrap">
            <div style={{ minWidth: 160 }}>
              <Select label="Esito" value={outSigned} onChange={e => setOutSigned(e.currentTarget.value)}>
                <option value="true">Firmato</option>
                <option value="false">Non firmato</option>
              </Select>
            </div>
            <div style={{ minWidth: 160 }}>
              <DateField kind="date" label="Data" value={outDate} max={todayIso()} onChange={e => setOutDate(e.currentTarget.value)} />
            </div>
            <div style={{ minWidth: 220, flex: 1 }}>
              <TextField label="Nota (facoltativa)" maxLength={1000} value={outNote} onChange={e => setOutNote(e.currentTarget.value)} />
            </div>
            <Button variant="tonal" icon="task_alt" type="submit" loading={busy} disabled={!outDate}>Registra esito</Button>
          </form>
        )}
      </CardContent>
      <DataTable variant="plain" aria-label="Firme del contratto">
        <thead>
          <tr><th>Creata</th><th>Stato</th><th>Documento</th><th>Firmata / esito</th><th>Impronta PDF firmato</th><th /></tr>
        </thead>
        <tbody>
          {data.signatures.length === 0 && <DataTableEmptyRow colSpan={6} title="Nessuna firma registrata." />}
          {data.signatures.map(s => (
            <tr key={s.id}>
              <td>{dateIt(s.createdAt)}</td>
              <td><StatusPill tone={STATUS_TONE[s.status]}>{STATUS_LABEL[s.status] ?? s.status}</StatusPill></td>
              <td>{s.documentName ?? '—'}</td>
              <td>
                {s.signedAt ? dateIt(s.signedAt) : s.outcomeDate ? dateIt(s.outcomeDate) : '—'}
                {s.note && <div className="text-muted md-typescale-body-small">{s.note}</div>}
              </td>
              <td><code className="md-typescale-body-small">{s.signedPdfHash ? `${s.signedPdfHash.slice(0, 16)}…` : '—'}</code></td>
              <td className="text-end text-nowrap">
                {s.evidenceCount > 0 && <Button variant="text" onClick={() => void showEvidence(s.id)}>Registro</Button>}
                {s.signedDocumentId && <IconButtonLink href={documentsApi.contentUrl(s.signedDocumentId)} icon="download" aria-label="Scarica il PDF firmato" size="sm" />}
                {s.status === 'InAttesa' && <Button variant="text" tone="error" disabled={busy} onClick={() => void revoke(s.id)}>Revoca</Button>}
              </td>
            </tr>
          ))}
        </tbody>
      </DataTable>
      {evidence && (
        <CardContent>
          <Callout tone={evidence.chainValid ? 'success' : 'error'} title={evidence.chainValid ? 'Registro delle prove integro' : 'Registro delle prove NON integro'}>
            Ogni evento include l'impronta del precedente: una modifica successiva rompe la catena.
          </Callout>
          <DataTable variant="plain" aria-label="Registro delle prove">
            <thead><tr><th>#</th><th>Evento</th><th>Quando</th><th>IP</th><th>Impronta</th></tr></thead>
            <tbody>
              {evidence.events.map(ev => (
                <tr key={ev.sequence}>
                  <td className="md-num">{ev.sequence}</td>
                  <td>{ev.action}</td>
                  <td>{new Date(ev.occurredAt).toLocaleString('it-IT')}</td>
                  <td>{ev.ip ?? '—'}</td>
                  <td><code className="md-typescale-body-small">{ev.hash.slice(0, 16)}…</code></td>
                </tr>
              ))}
            </tbody>
          </DataTable>
          <Button variant="text" className="mt-2" onClick={() => setEvidence(null)}>Chiudi registro</Button>
        </CardContent>
      )}
    </Card>
  )
}
